/**
 * Security Settings panel — full hub-core auth surface (D2 + expanded).
 *
 * Renders inside the User Settings overlay → Security tab. Sections:
 *
 * 1. Account Key — seed phrase info + rotate
 * 2. PIN — set / change / remove (4-8 digits, for sensitive actions)
 * 3. Two-Factor Authentication — TOTP enable/disable, backup codes
 * 4. Known Devices — browsers that signed in, trust/revoke/rename
 * 5. Active Sessions — live sessions, revoke individual / all others
 *
 * All endpoints target hub-core's /api/auth/* surface. Hub-people will
 * need a backend proxy or its own implementation before these go live.
 */

import { showToast } from './components/toast';

// ─── Types ──────────────────────────────────────────────────────────

interface TwoFaStatus {
  enabled: boolean;
  enrolled_at: string | null;
  backup_codes_remaining: number;
}

interface PinStatus {
  has_pin: boolean;
}

interface KnownDevice {
  id: string;
  name: string | null;
  fingerprint: string | null;
  user_agent: string | null;
  ip: string | null;
  trusted: boolean;
  first_seen: string;
  last_seen: string;
}

interface SessionRow {
  id: string;
  device_id: string;
  device_name: string | null;
  created_at: string;
  last_active: string | null;
  current: boolean;
}

// ─── API helpers ────────────────────────────────────────────────────

async function authGet<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(path, { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json();
    return (json.data ?? null) as T;
  } catch { return null; }
}

async function authPost<T>(path: string, body: Record<string, unknown> = {}): Promise<{ ok: boolean; data: T | null; error: string | null }> {
  try {
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (json.status === 'ok') return { ok: true, data: json.data ?? null, error: null };
    return { ok: false, data: null, error: json.error || 'Request failed' };
  } catch (e) {
    return { ok: false, data: null, error: String(e) };
  }
}

async function authDelete(path: string): Promise<{ ok: boolean; error: string | null }> {
  try {
    const res = await fetch(path, { method: 'DELETE', credentials: 'include' });
    const json = await res.json();
    if (json.status === 'ok') return { ok: true, error: null };
    return { ok: false, error: json.error || 'Request failed' };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

// ─── Main render ────────────────────────────────────────────────────

export async function renderSecuritySettings(container: HTMLElement): Promise<void> {
  container.innerHTML = '<div class="sec-loading">Loading security settings...</div>';

  const [twofa, pin] = await Promise.all([
    authGet<TwoFaStatus>('/api/auth/2fa/status'),
    authGet<PinStatus>('/api/auth/pin/status'),
  ]);

  const tfStatus: TwoFaStatus = twofa ?? { enabled: false, enrolled_at: null, backup_codes_remaining: 0 };
  const pinStatus: PinStatus = pin ?? { has_pin: false };

  container.innerHTML = `
    <div class="sec-container">
      ${renderAccountKey()}
      ${renderPin(pinStatus)}
      ${renderTwoFactor(tfStatus)}
      ${renderDevicesSection()}
      ${renderSessionsSection()}
    </div>`;

  wireAccountKey(container);
  wirePin(container, pinStatus);
  wireTwoFactor(container, tfStatus);
  void loadDevices(container);
  void loadSessions(container);
}

// ─── 1. Account Key (seed phrase) ───────────────────────────────────

function renderAccountKey(): string {
  return `
    <div class="sec-section">
      <div class="sec-section-title">Account Key</div>
      <div class="sec-section-desc">
        Your account is secured with a 12-word seed phrase. This phrase is the master key to your identity — store it somewhere safe and never share it.
      </div>
      <div class="sec-method">
        <div class="sec-method-header">
          <div class="sec-method-icon">${keyIcon()}</div>
          <div class="sec-method-info">
            <div class="sec-method-title">Seed Phrase</div>
            <div class="sec-method-status sec-status-on">Active</div>
          </div>
          <button class="btn btn-sm btn-danger-outline" id="sec-seed-rotate">Rotate Seed</button>
        </div>
        <div class="sec-method-desc">
          Rotating generates a new 12-word phrase and kills all active sessions. You will need to sign in again on every device.
        </div>
      </div>
    </div>`;
}

function wireAccountKey(root: HTMLElement): void {
  root.querySelector('#sec-seed-rotate')?.addEventListener('click', () => {
    showDangerConfirm(
      'Rotate Seed Phrase',
      'This will generate a new 12-word seed phrase and immediately sign you out of every device. Your old seed phrase will no longer work. This cannot be undone.',
      'Rotate & Sign Out',
      async () => {
        const res = await authPost('/api/auth/seed/rotate');
        if (res.ok) {
          showToast('Seed rotated — you will be signed out', 'ok');
          setTimeout(() => { window.location.href = '/'; }, 1500);
        } else {
          showToast(res.error || 'Failed to rotate seed', 'err');
        }
      }
    );
  });
}

// ─── 2. PIN ─────────────────────────────────────────────────────────

function renderPin(status: PinStatus): string {
  return `
    <div class="sec-section">
      <div class="sec-section-title">PIN</div>
      <div class="sec-section-desc">
        A 4–8 digit PIN protects sensitive actions like rotating your seed phrase, managing 2FA, and revoking sessions.
      </div>
      <div class="sec-method${status.has_pin ? ' sec-method-active' : ''}">
        <div class="sec-method-header">
          <div class="sec-method-icon">${pinIcon()}</div>
          <div class="sec-method-info">
            <div class="sec-method-title">Account PIN</div>
            <div class="sec-method-status ${status.has_pin ? 'sec-status-on' : 'sec-status-off'}">
              ${status.has_pin ? 'Set' : 'Not set'}
            </div>
          </div>
          <div class="sec-pin-actions">
            <button class="btn btn-sm ${status.has_pin ? 'btn-secondary' : 'btn-primary'}" id="sec-pin-set">
              ${status.has_pin ? 'Change PIN' : 'Set PIN'}
            </button>
            ${status.has_pin ? '<button class="btn btn-sm btn-danger-outline" id="sec-pin-remove">Remove</button>' : ''}
          </div>
        </div>
      </div>
    </div>`;
}

function wirePin(root: HTMLElement, status: PinStatus): void {
  root.querySelector('#sec-pin-set')?.addEventListener('click', () => {
    showPinSetModal(status.has_pin, root);
  });

  root.querySelector('#sec-pin-remove')?.addEventListener('click', () => {
    showCodePrompt('Enter your current PIN to remove it', async (code) => {
      const res = await authPost('/api/auth/pin/remove', { current_pin: code });
      if (res.ok) {
        showToast('PIN removed', 'ok');
        void renderSecuritySettings(root);
      } else {
        showToast(res.error || 'Failed to remove PIN', 'err');
      }
    }, { maxLength: 8, inputMode: 'numeric', placeholder: 'Current PIN' });
  });
}

function showPinSetModal(hasExisting: boolean, parentContainer: HTMLElement): void {
  const overlay = document.createElement('div');
  overlay.className = 'sec-overlay';
  overlay.innerHTML = `
    <div class="sec-enroll-modal">
      <h3>${hasExisting ? 'Change PIN' : 'Set PIN'}</h3>
      <p>Choose a 4–8 digit PIN. You will need this for sensitive account actions.</p>
      ${hasExisting ? `
        <div class="sec-pin-field">
          <label class="sec-pin-label">Current PIN</label>
          <input type="password" id="sec-pin-current" class="form-input" maxlength="8"
            placeholder="Current PIN" inputmode="numeric" autocomplete="off">
        </div>` : ''}
      <div class="sec-pin-field">
        <label class="sec-pin-label">New PIN</label>
        <input type="password" id="sec-pin-new" class="form-input" maxlength="8"
          placeholder="4–8 digits" inputmode="numeric" autocomplete="off">
      </div>
      <div class="sec-pin-field">
        <label class="sec-pin-label">Confirm PIN</label>
        <input type="password" id="sec-pin-confirm" class="form-input" maxlength="8"
          placeholder="Repeat" inputmode="numeric" autocomplete="off">
      </div>
      <div id="sec-pin-error" class="sec-pin-error" style="display:none"></div>
      <div style="display:flex;gap:8px;margin-top:16px;justify-content:flex-end">
        <button class="btn btn-sm btn-secondary" id="sec-pin-cancel">Cancel</button>
        <button class="btn btn-sm btn-primary" id="sec-pin-submit">${hasExisting ? 'Change' : 'Set PIN'}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  overlay.querySelector('#sec-pin-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#sec-pin-submit')?.addEventListener('click', async () => {
    const newPin = (document.getElementById('sec-pin-new') as HTMLInputElement)?.value ?? '';
    const confirm = (document.getElementById('sec-pin-confirm') as HTMLInputElement)?.value ?? '';
    const currentPin = hasExisting ? (document.getElementById('sec-pin-current') as HTMLInputElement)?.value ?? '' : '';
    const errorEl = document.getElementById('sec-pin-error');

    if (newPin.length < 4 || newPin.length > 8 || !/^\d+$/.test(newPin)) {
      if (errorEl) { errorEl.textContent = 'PIN must be 4–8 digits'; errorEl.style.display = ''; }
      return;
    }
    if (newPin !== confirm) {
      if (errorEl) { errorEl.textContent = 'PINs do not match'; errorEl.style.display = ''; }
      return;
    }

    const body: Record<string, unknown> = { new_pin: newPin };
    if (hasExisting) body['current_pin'] = currentPin;

    const res = await authPost('/api/auth/pin/set', body);
    if (res.ok) {
      overlay.remove();
      showToast('PIN updated', 'ok');
      void renderSecuritySettings(parentContainer);
    } else {
      if (errorEl) { errorEl.textContent = res.error || 'Failed'; errorEl.style.display = ''; }
    }
  });
}

// ─── 3. Two-Factor Authentication ───────────────────────────────────

function renderTwoFactor(status: TwoFaStatus): string {
  const totpSection = status.enabled
    ? `<div class="sec-method sec-method-active">
        <div class="sec-method-header">
          <div class="sec-method-icon">${lockIcon()}</div>
          <div class="sec-method-info">
            <div class="sec-method-title">Authenticator App</div>
            <div class="sec-method-status sec-status-on">Enabled${status.enrolled_at ? ` since ${status.enrolled_at.slice(0, 10)}` : ''}</div>
          </div>
          <button class="btn btn-sm btn-secondary" id="sec-totp-disable">Disable</button>
        </div>
        <div class="sec-method-detail">
          <span class="sec-backup-count">${status.backup_codes_remaining} backup code${status.backup_codes_remaining !== 1 ? 's' : ''} remaining</span>
          <button class="btn btn-sm btn-secondary" id="sec-regen-backup">Regenerate Codes</button>
        </div>
      </div>`
    : `<div class="sec-method">
        <div class="sec-method-header">
          <div class="sec-method-icon">${lockIcon()}</div>
          <div class="sec-method-info">
            <div class="sec-method-title">Authenticator App</div>
            <div class="sec-method-status sec-status-off">Not enabled</div>
          </div>
          <button class="btn btn-sm btn-primary" id="sec-totp-enable">Enable</button>
        </div>
        <div class="sec-method-desc">Use Google Authenticator, 1Password, or Authy to generate time-based codes.</div>
      </div>`;

  return `
    <div class="sec-section">
      <div class="sec-section-title">Two-Factor Authentication</div>
      <div class="sec-section-desc">Add an extra layer of security to your account.</div>
      <div class="sec-methods">
        ${totpSection}
        <div class="sec-method">
          <div class="sec-method-header">
            <div class="sec-method-icon">${emailIcon()}</div>
            <div class="sec-method-info">
              <div class="sec-method-title">Email 2FA</div>
              <div class="sec-method-status sec-status-off">Coming soon</div>
            </div>
            <button class="btn btn-sm btn-secondary" disabled>Enable</button>
          </div>
          <div class="sec-method-desc">Receive a 6-digit code via email when signing in.</div>
        </div>
        <div class="sec-method">
          <div class="sec-method-header">
            <div class="sec-method-icon">${phoneSmIcon()}</div>
            <div class="sec-method-info">
              <div class="sec-method-title">SMS 2FA</div>
              <div class="sec-method-status sec-status-off">Coming soon</div>
            </div>
            <button class="btn btn-sm btn-secondary" disabled>Enable</button>
          </div>
          <div class="sec-method-desc">Receive an SMS code when signing in via hub-cell.</div>
        </div>
      </div>
    </div>`;
}

function wireTwoFactor(root: HTMLElement, status: TwoFaStatus): void {
  if (!status.enabled) {
    root.querySelector('#sec-totp-enable')?.addEventListener('click', async () => {
      const res = await authPost<{ secret_b32: string; otpauth_uri: string; backup_codes: string[] }>('/api/auth/2fa/enroll');
      if (res.ok && res.data) {
        showEnrollmentModal(res.data, root);
      } else {
        showToast(res.error || 'Enrollment failed', 'err');
      }
    });
  } else {
    root.querySelector('#sec-totp-disable')?.addEventListener('click', () => {
      showCodePrompt('Enter a TOTP code or backup code to disable 2FA', async (code) => {
        const res = await authPost('/api/auth/2fa/disable', { code });
        if (res.ok) {
          showToast('2FA disabled', 'ok');
          void renderSecuritySettings(root);
        } else {
          showToast(res.error || 'Invalid code', 'err');
        }
      });
    });

    root.querySelector('#sec-regen-backup')?.addEventListener('click', () => {
      showCodePrompt('Enter a TOTP code to regenerate backup codes', async (code) => {
        const res = await authPost<{ backup_codes: string[] }>('/api/auth/2fa/regenerate-backup-codes', { code });
        if (res.ok && res.data?.backup_codes) {
          showBackupCodesModal(res.data.backup_codes);
          void renderSecuritySettings(root);
        } else {
          showToast(res.error || 'Invalid code', 'err');
        }
      });
    });
  }
}

// ─── 4. Known Devices ───────────────────────────────────────────────

function renderDevicesSection(): string {
  return `
    <div class="sec-section">
      <div class="sec-section-title">Known Devices</div>
      <div class="sec-section-desc">Browsers and devices that have signed in to your account. Untrusted devices trigger a sign-in alert.</div>
      <div id="sec-devices"><div class="sec-loading">Loading devices...</div></div>
    </div>`;
}

async function loadDevices(root: HTMLElement): Promise<void> {
  const el = root.querySelector('#sec-devices');
  if (!el) return;

  const devices = await authGet<KnownDevice[]>('/api/auth/devices');
  if (!devices || devices.length === 0) {
    el.innerHTML = '<div class="sec-empty">No known devices.</div>';
    return;
  }

  el.innerHTML = devices.map(d => {
    const nameDisplay = d.name || deviceNameFromUA(d.user_agent) || 'Unknown device';
    const fpShort = d.fingerprint ? d.fingerprint.slice(0, 12) : '';
    const trustBadge = d.trusted
      ? '<span class="sec-device-badge sec-badge-trusted">Trusted</span>'
      : '<span class="sec-device-badge sec-badge-untrusted">Untrusted</span>';

    return `
      <div class="sec-device-row" data-device-id="${esc(d.id)}">
        <div class="sec-device-icon">${monitorIcon()}</div>
        <div class="sec-device-info">
          <div class="sec-device-name">
            ${esc(nameDisplay)} ${trustBadge}
          </div>
          <div class="sec-device-meta">
            ${fpShort ? `<span title="${esc(d.fingerprint ?? '')}">FP: ${esc(fpShort)}...</span>` : ''}
            ${d.ip ? `<span>${esc(d.ip)}</span>` : ''}
            <span>Last seen ${esc(relTime(d.last_seen))}</span>
          </div>
        </div>
        <div class="sec-device-actions">
          ${d.trusted
            ? `<button class="btn btn-sm btn-secondary sec-device-revoke" data-device-id="${esc(d.id)}">Revoke Trust</button>`
            : `<button class="btn btn-sm btn-primary sec-device-trust" data-device-id="${esc(d.id)}">Trust</button>`
          }
          <button class="btn btn-sm btn-secondary sec-device-rename" data-device-id="${esc(d.id)}" title="Rename">${editIcon()}</button>
        </div>
      </div>`;
  }).join('');

  el.querySelectorAll<HTMLButtonElement>('.sec-device-trust').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset['deviceId'];
      if (!id) return;
      btn.disabled = true;
      const res = await authPost(`/api/auth/devices/${encodeURIComponent(id)}/trust`);
      if (res.ok) { void loadDevices(root); }
      else { showToast(res.error || 'Failed', 'err'); btn.disabled = false; }
    });
  });

  el.querySelectorAll<HTMLButtonElement>('.sec-device-revoke').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset['deviceId'];
      if (!id) return;
      btn.disabled = true;
      const res = await authPost(`/api/auth/devices/${encodeURIComponent(id)}/revoke`);
      if (res.ok) { void loadDevices(root); }
      else { showToast(res.error || 'Failed', 'err'); btn.disabled = false; }
    });
  });

  el.querySelectorAll<HTMLButtonElement>('.sec-device-rename').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['deviceId'];
      if (!id) return;
      showCodePrompt('Enter a new name for this device', async (name) => {
        const res = await authPost(`/api/auth/devices/${encodeURIComponent(id)}/name`, { name });
        if (res.ok) { void loadDevices(root); }
        else { showToast(res.error || 'Failed', 'err'); }
      }, { placeholder: 'My Laptop', maxLength: 64 });
    });
  });
}

// ─── 5. Active Sessions ─────────────────────────────────────────────

function renderSessionsSection(): string {
  return `
    <div class="sec-section">
      <div class="sec-section-title">Active Sessions</div>
      <div class="sec-section-desc">Manage where you are currently signed in.</div>
      <div id="sec-sessions"><div class="sec-loading">Loading sessions...</div></div>
      <div id="sec-sessions-actions" style="display:none">
        <button class="btn btn-sm btn-danger-outline" id="sec-revoke-all-others">Sign Out All Other Sessions</button>
      </div>
    </div>`;
}

async function loadSessions(root: HTMLElement): Promise<void> {
  const el = root.querySelector('#sec-sessions');
  const actionsEl = root.querySelector('#sec-sessions-actions');
  if (!el) return;

  const sessions = await authGet<SessionRow[]>('/api/auth/sessions');
  if (!sessions || sessions.length === 0) {
    el.innerHTML = '<div class="sec-empty">No active sessions.</div>';
    return;
  }

  el.innerHTML = sessions.map(s => {
    const name = s.device_name || s.device_id || 'Unknown device';
    const currentTag = s.current ? '<span class="sec-session-current">This device</span>' : '';
    const timeLabel = s.last_active ? `Active ${relTime(s.last_active)}` : `Created ${relTime(s.created_at)}`;

    return `
      <div class="sec-session-row">
        <div class="sec-session-info">
          <div class="sec-session-device">${esc(name)} ${currentTag}</div>
          <div class="sec-session-time">${esc(timeLabel)}</div>
        </div>
        ${s.current
          ? ''
          : `<button class="btn btn-sm btn-secondary sec-revoke" data-session-id="${esc(s.id)}">Revoke</button>`
        }
      </div>`;
  }).join('');

  const hasOthers = sessions.some(s => !s.current);
  if (actionsEl instanceof HTMLElement) {
    actionsEl.style.display = hasOthers ? '' : 'none';
  }

  el.querySelectorAll<HTMLButtonElement>('.sec-revoke').forEach(btn => {
    btn.addEventListener('click', async () => {
      const sid = btn.dataset['sessionId'];
      if (!sid) return;
      btn.disabled = true;
      btn.textContent = 'Revoking...';
      const res = await authDelete(`/api/auth/sessions/${encodeURIComponent(sid)}`);
      if (res.ok) { void loadSessions(root); }
      else { showToast(res.error || 'Failed', 'err'); btn.disabled = false; btn.textContent = 'Revoke'; }
    });
  });

  root.querySelector('#sec-revoke-all-others')?.addEventListener('click', () => {
    showDangerConfirm(
      'Sign Out All Other Sessions',
      'This will immediately sign you out of every other device. Only this browser session will remain active.',
      'Sign Out Others',
      async () => {
        const res = await authDelete('/api/auth/sessions/others');
        if (res.ok) {
          showToast('All other sessions revoked', 'ok');
          void loadSessions(root);
        } else {
          showToast(res.error || 'Failed', 'err');
        }
      }
    );
  });
}

// ─── Modals & prompts ───────────────────────────────────────────────

function showEnrollmentModal(data: { secret_b32: string; otpauth_uri: string; backup_codes: string[] }, parentContainer: HTMLElement): void {
  const overlay = document.createElement('div');
  overlay.className = 'sec-overlay';
  overlay.innerHTML = `
    <div class="sec-enroll-modal">
      <h3>Set up Authenticator App</h3>
      <p>Scan this QR code with your authenticator app, or enter the key manually:</p>
      <div class="sec-secret-display">
        <code>${esc(data.secret_b32)}</code>
      </div>
      <p style="margin-top:16px">Enter the 6-digit code from your app to verify:</p>
      <input type="text" id="sec-verify-code" class="form-input" maxlength="6"
        placeholder="123456" inputmode="numeric"
        style="text-align:center;font-size:20px;letter-spacing:0.2em" />
      <div style="display:flex;gap:8px;margin-top:16px;justify-content:flex-end">
        <button class="btn btn-sm btn-secondary" id="sec-enroll-cancel">Cancel</button>
        <button class="btn btn-sm btn-primary" id="sec-enroll-verify">Verify</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  overlay.querySelector('#sec-enroll-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#sec-enroll-verify')?.addEventListener('click', async () => {
    const code = (document.getElementById('sec-verify-code') as HTMLInputElement)?.value?.trim();
    if (!code) return;
    const res = await authPost('/api/auth/2fa/verify', { code });
    if (res.ok) {
      overlay.remove();
      showBackupCodesModal(data.backup_codes);
      void renderSecuritySettings(parentContainer);
    } else {
      showToast(res.error || 'Invalid code', 'err');
    }
  });
}

function showBackupCodesModal(codes: string[]): void {
  const overlay = document.createElement('div');
  overlay.className = 'sec-overlay';
  overlay.innerHTML = `
    <div class="sec-enroll-modal">
      <h3>Backup Codes</h3>
      <p>Save these codes in a safe place. Each code can be used once to sign in if you lose access to your authenticator app.</p>
      <div class="sec-backup-grid">
        ${codes.map(c => `<code class="sec-backup-code">${esc(c)}</code>`).join('')}
      </div>
      <div style="display:flex;gap:8px;margin-top:16px;justify-content:flex-end">
        <button class="btn btn-sm btn-secondary" id="sec-backup-copy">Copy All</button>
        <button class="btn btn-sm btn-secondary" id="sec-backup-download">Download</button>
        <button class="btn btn-sm btn-primary" id="sec-backup-done">Done</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  overlay.querySelector('#sec-backup-done')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#sec-backup-download')?.addEventListener('click', () => {
    const text = codes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hub-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  });
  overlay.querySelector('#sec-backup-copy')?.addEventListener('click', () => {
    void navigator.clipboard.writeText(codes.join('\n')).then(
      () => showToast('Copied to clipboard', 'ok'),
      () => showToast('Copy failed', 'err'),
    );
  });
}

function showCodePrompt(
  message: string,
  onSubmit: (code: string) => Promise<void>,
  opts?: { maxLength?: number; inputMode?: string; placeholder?: string }
): void {
  const overlay = document.createElement('div');
  overlay.className = 'sec-overlay';
  overlay.innerHTML = `
    <div class="sec-enroll-modal">
      <p>${esc(message)}</p>
      <input type="text" id="sec-prompt-code" class="form-input"
        maxlength="${opts?.maxLength ?? 16}"
        placeholder="${esc(opts?.placeholder ?? 'Code')}"
        inputmode="${esc(opts?.inputMode ?? 'text')}"
        style="text-align:center;font-size:18px;letter-spacing:0.1em" />
      <div style="display:flex;gap:8px;margin-top:16px;justify-content:flex-end">
        <button class="btn btn-sm btn-secondary" id="sec-prompt-cancel">Cancel</button>
        <button class="btn btn-sm btn-primary" id="sec-prompt-submit">Submit</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const input = document.getElementById('sec-prompt-code') as HTMLInputElement | null;
  input?.focus();
  input?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); overlay.querySelector<HTMLButtonElement>('#sec-prompt-submit')?.click(); }
  });

  overlay.querySelector('#sec-prompt-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#sec-prompt-submit')?.addEventListener('click', async () => {
    const code = input?.value?.trim();
    if (!code) return;
    const btn = overlay.querySelector('#sec-prompt-submit') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Verifying...'; }
    await onSubmit(code);
    overlay.remove();
  });
}

function showDangerConfirm(title: string, message: string, confirmLabel: string, onConfirm: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'sec-overlay';
  overlay.innerHTML = `
    <div class="sec-enroll-modal">
      <h3 style="color:var(--status-dnd, #dc2626)">${esc(title)}</h3>
      <p>${esc(message)}</p>
      <div style="display:flex;gap:8px;margin-top:16px;justify-content:flex-end">
        <button class="btn btn-sm btn-secondary" id="sec-danger-cancel">Cancel</button>
        <button class="btn btn-sm btn-danger" id="sec-danger-confirm">${esc(confirmLabel)}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  overlay.querySelector('#sec-danger-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#sec-danger-confirm')?.addEventListener('click', async () => {
    const btn = overlay.querySelector('#sec-danger-confirm') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Processing...'; }
    await onConfirm();
    overlay.remove();
  });
}

// ─── Helpers ────────────────────────────────────────────────────────

function deviceNameFromUA(ua: string | null): string | null {
  if (!ua) return null;
  const browser = /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox'
    : /Safari/.test(ua) ? 'Safari'
    : /Edg/.test(ua) ? 'Edge'
    : null;
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : null;
  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return os;
  return null;
}

function relTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

// ─── Icons ──────────────────────────────────────────────────────────

function lockIcon(): string {
  return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
}

function keyIcon(): string {
  return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>';
}

function pinIcon(): string {
  return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1"/></svg>';
}

function emailIcon(): string {
  return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>';
}

function phoneSmIcon(): string {
  return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>';
}

function monitorIcon(): string {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>';
}

function editIcon(): string {
  return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
}
